import React from 'react';
import { 
    Crown, 
    Check, 
    X, 
    Star, 
    Sparkles,
    Monitor,
    Upload,
    Scissors,
    RotateCcw,
    FileText,
    MessageSquare,
    Palette
} from 'lucide-react';

interface Tier {
    id: number;
    name: string;
    price: number;
    maxVodDuration: number;
    dailyVodUploadLimit: number;
    weeklyVodUploadLimit: number;
    monthlyVodUploadLimit: number;
    maxClipsPerVod: number;
    ffmpegResolution: string;
    ffmpegFps: number;
    editorResolution: string;
    editorFps: number;
    customAiPrompt: boolean;
    monthlyRedetectionLimit: number;
}

interface ModernSelectablePricingSectionProps {
    tiers: Tier[];
    selectedTier?: number | null;
    activeTier?: number | null;
    onSelectTier: (tierId: number) => void;
    isUpgradeDisallowed?: (tierId: number) => boolean;
    compact?: boolean;
}

const SelectablePricingCard: React.FC<{
    tier: Tier;
    isSelected?: boolean;
    isActive?: boolean;
    isUpgradeDisallowed?: boolean;
    onSelect: () => void;
    compact?: boolean;
}> = ({ tier, isSelected, isActive, isUpgradeDisallowed, onSelect, compact = false }) => {
    const formatDuration = (seconds: number) => {
        if (seconds >= 999999 || seconds === -1) return 'Unlimited';
        const hours = Math.floor(seconds / 3600);
        return `${hours} hours`;
    };

    const formatLimit = (limit: number) => {
        return limit >= 999999 || limit === -1 ? 'Unlimited' : limit.toString();
    };

    const getFeatureIcon = (feature: string) => {
        switch (feature) {
            case 'duration': return <Monitor size={16} />;
            case 'upload': return <Upload size={16} />;
            case 'clips': return <Scissors size={16} />;
            case 'redetection': return <RotateCcw size={16} />;
            case 'prompt': return <MessageSquare size={16} />;
            case 'templates': return <FileText size={16} />;
            case 'quality': return <Monitor size={16} />;
            default: return <Check size={16} />;
        }
    };

    const isPopular = tier.name === 'Tier 2';

    const features = [
        {
            id: 'duration',
            label: 'Max VOD Duration',
            value: formatDuration(tier.maxVodDuration),
            available: tier.maxVodDuration > 0 || tier.maxVodDuration === -1,
            icon: 'duration'
        },
        {
            id: 'daily',
            label: 'Daily Uploads',
            value: formatLimit(tier.dailyVodUploadLimit),
            available: tier.dailyVodUploadLimit > 0 || tier.dailyVodUploadLimit === -1,
            icon: 'upload'
        },
        {
            id: 'monthly',
            label: 'Monthly Uploads',
            value: formatLimit(tier.monthlyVodUploadLimit),
            available: tier.monthlyVodUploadLimit > 0 || tier.monthlyVodUploadLimit === -1,
            icon: 'upload'
        },
        {
            id: 'clips',
            label: 'Clips per VOD',
            value: formatLimit(tier.maxClipsPerVod),
            available: tier.maxClipsPerVod > 0 || tier.maxClipsPerVod === -1,
            icon: 'clips'
        },
        {
            id: 'quality',
            label: 'Video Quality',
            value: tier.editorResolution === '3840x2160' ? '4K 60fps' : '1080p 60fps',
            available: true, // All tiers have at least 1080p60fps
            icon: 'quality'
        },
        {
            id: 'prompt',
            label: 'Custom AI Prompts',
            value: tier.price === 0 ? '2 uses' : 'Unlimited',
            available: true, // All tiers have some access
            icon: 'prompt'
        },
        {
            id: 'templates',
            label: 'Custom Save Templates',
            value: tier.price >= 20 ? 'Available' : 'Not Available',
            available: tier.price >= 20, // Tier 2 and Tier 3 only
            icon: 'templates'
        },
        {
            id: 'redetection',
            label: 'Monthly Redetections',
            value: formatLimit(tier.monthlyRedetectionLimit),
            available: tier.monthlyRedetectionLimit > 0 || tier.monthlyRedetectionLimit === -1,
            icon: 'redetection'
        }
    ];

    return (
        <div className={`
            relative group h-full cursor-pointer
            ${isPopular && !compact 
                ? 'scale-105 z-10' 
                : 'scale-100'
            }
            ${compact ? 'transform-none scale-100' : ''}
        `}>
            {/* Glow effect for popular plan */}
            {isPopular && !compact && (
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 rounded-2xl opacity-20 blur-xl group-hover:opacity-30 transition-opacity duration-300" />
            )}
            
            <div 
                className={`
                    relative h-full flex flex-col
                    bg-black/40 backdrop-blur-xl border
                    rounded-2xl overflow-hidden
                    transition-all duration-300 ease-out
                    hover:bg-black/50
                    ${isSelected || isActive
                        ? 'border-purple-500 ring-2 ring-purple-500/50 bg-black/60'
                        : 'border-white/10 hover:border-white/20'
                    }
                    ${isPopular ? 'ring-2 ring-purple-500/30' : ''}
                    ${isUpgradeDisallowed ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                onClick={!isUpgradeDisallowed ? onSelect : undefined}
            >
                {/* Popular badge */}
                {isPopular && (
                    <div className="absolute -top-0.5 left-1/2 transform -translate-x-1/2 z-20">
                        <div className="
                            flex items-center gap-1 px-4 py-2 rounded-full text-xs font-medium
                            bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg
                        ">
                            <Star size={12} className="fill-white" />
                            Most Popular
                        </div>
                    </div>
                )}

                {/* Active badge */}
                {isActive && (
                    <div className="absolute top-4 right-4 z-20">
                        <div className="
                            flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium
                            bg-green-500/20 border border-green-500/50 text-green-400
                        ">
                            <Check size={12} />
                            Active
                        </div>
                    </div>
                )}

                <div className={`p-6 ${isPopular ? 'pt-8' : ''} flex-1 flex flex-col`}>
                    {/* Header */}
                    <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-white mb-2">
                            {tier.name}
                        </h3>
                        <div className="flex items-baseline justify-center gap-1">
                            <span className="text-4xl font-bold text-white">
                                ${tier.price}
                            </span>
                            {tier.price > 0 && (
                                <span className="text-white/60 text-sm">/month</span>
                            )}
                        </div>
                        {tier.price === 0 && (
                            <p className="text-white/60 text-sm mt-1">Forever free</p>
                        )}
                    </div>

                    {/* Features */}
                    <div className="flex-1 space-y-3 mb-6">
                        {features.map((feature) => (
                            <div key={feature.id} className="flex items-center gap-3">
                                <div className={`
                                    flex items-center justify-center w-5 h-5 rounded-full
                                    ${feature.available 
                                        ? 'bg-green-500/20 text-green-400' 
                                        : 'bg-red-500/20 text-red-400'
                                    }
                                `}>
                                    {feature.available ? (
                                        getFeatureIcon(feature.icon)
                                    ) : (
                                        <X size={12} />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                        <span className="text-white/80 text-sm truncate">
                                            {feature.label}
                                        </span>
                                        <span className={`
                                            text-sm font-medium
                                            ${feature.available ? 'text-white' : 'text-white/40'}
                                        `}>
                                            {feature.value}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Selection indicator */}
                    <div className="mt-auto">
                        <div className={`
                            w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl
                            font-medium transition-all duration-200 text-center
                            ${isSelected
                                ? 'bg-purple-500 text-white'
                                : isActive
                                ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                                : isUpgradeDisallowed
                                ? 'bg-red-500/20 border border-red-500/50 text-red-400'
                                : 'bg-white/10 border border-white/20 text-white hover:bg-white/15'
                            }
                        `}>
                            {isActive ? (
                                <>
                                    <Check size={16} />
                                    Current Plan
                                </>
                            ) : isSelected ? (
                                <>
                                    <Crown size={16} />
                                    Selected
                                </>
                            ) : isUpgradeDisallowed ? (
                                <>
                                    <X size={16} />
                                    Upgrade Restricted
                                </>
                            ) : (
                                <>
                                    <Crown size={16} />
                                    Select Plan
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ModernSelectablePricingSection: React.FC<ModernSelectablePricingSectionProps> = ({
    tiers,
    selectedTier,
    activeTier,
    onSelectTier,
    isUpgradeDisallowed = () => false,
    compact = false
}) => {
    return (
        <div className="relative">
            {/* Background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
            </div>

            <div className="relative">
                {/* Header */}
                {!compact && (
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm font-medium mb-4">
                            <Sparkles size={16} />
                            Choose Your Plan
                        </div>
                        <h2 className="text-4xl font-bold text-white mb-4">
                            Subscription Plans
                        </h2>
                        <p className="text-xl text-white/60 max-w-2xl mx-auto">
                            Select the plan that fits your content creation needs.
                        </p>
                    </div>
                )}

                {/* Pricing Grid */}
                <div className={`
                    grid gap-6
                    ${compact 
                        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' 
                        : 'grid-cols-1 lg:grid-cols-3'
                    }
                    max-w-7xl mx-auto
                `}>
                    {tiers.map((tier) => (
                        <SelectablePricingCard
                            key={tier.id}
                            tier={tier}
                            isSelected={selectedTier === tier.id}
                            isActive={activeTier === tier.id}
                            isUpgradeDisallowed={isUpgradeDisallowed(tier.id)}
                            onSelect={() => onSelectTier(tier.id)}
                            compact={compact}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ModernSelectablePricingSection;